import { BabyRecordSchema, EventRecordSchema, type BabyRecord, type EventRecord } from '@/lib/schemas';
import type { OutboxEntity, OutboxOp } from '@/db/db';

export type WireRecord = BabyRecord | EventRecord;
export interface Scope { householdId: string; userId: string; generation: number }
export interface OperationResult {
  operation_id: string; status: 'applied' | 'conflict' | 'rejected'; version: number;
  server_record: WireRecord | null; reason?: string;
}
export interface Delta { seq: string; household_id: string; entity_type: OutboxEntity; entity_id: string; record: WireRecord }
export interface Page { changes: Delta[]; next_cursor: string; has_more: boolean; retired_baby_ids?: string[] }
export interface SyncTransport {
  apply(householdId: string, operations: ReturnType<typeof wireOperation>[]): Promise<unknown>;
  pull(householdId: string, cursor: string): Promise<unknown>;
}
export class ScopeChanged extends Error { constructor() { super('Account or household changed.'); } }

// Preserve sub-millisecond PostgreSQL fractions when normalizing timezone offsets.
// CAS uses versions; retained tombstone timestamps must still compare exactly.
export function utcStamp(value: string): string {
  const fraction = /\.(\d+)(?:Z|[+-]\d{2}:\d{2})$/.exec(value)?.[1] ?? '';
  const digits = fraction.replace(/0+$/, '').padEnd(3, '0');
  return `${new Date(value).toISOString().slice(0, 19)}.${digits}Z`;
}
export function recordFromWire(entity: OutboxEntity, value: unknown, householdId: string): WireRecord {
  const record = entity === 'event' ? EventRecordSchema.parse(value) : BabyRecordSchema.parse(value);
  if (record.household_id !== householdId || record.version < 1) throw new Error('Invalid server record scope/version.');
  for (const key of ['created_at', 'updated_at', 'deleted_at', ...('type' in record ? ['started_at', 'ended_at'] : [])]) {
    const fields = record as unknown as Record<string, unknown>;
    if (typeof fields[key] === 'string') fields[key] = utcStamp(fields[key] as string);
  }
  return record;
}
export function wireOperation(op: OutboxOp) {
  return { operation_id: op.operation_id, household_id: op.household_id, entity: op.entity, entity_id: op.entity_id,
    op: op.op, base_version: op.base_version, data: op.data, client_ts: op.client_ts };
}
export function parseResult(value: unknown, op: OutboxOp): OperationResult {
  if (!Array.isArray(value) || value.length !== 1) throw new Error('Invalid upload acknowledgement.');
  const r = value[0] as OperationResult;
  if (!r || r.operation_id !== op.operation_id || !['applied', 'conflict', 'rejected'].includes(r.status)
    || !Number.isSafeInteger(r.version) || r.version < 0) throw new Error('Invalid upload acknowledgement.');
  const record = r.server_record == null ? null : recordFromWire(op.entity, r.server_record, op.household_id);
  if (record && (record.id !== op.entity_id || record.version !== r.version)) throw new Error('Acknowledgement identity mismatch.');
  if (!record && r.version !== 0) throw new Error('Missing server version record.');
  if (r.status === 'applied' && (!record || record.version !== op.base_version + 1)) throw new Error('Invalid applied version.');
  return { ...r, server_record: record };
}
const cursorPattern = /^\d+$/;
export function parseRetiredBabies(value: unknown): string[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 100_000 || value.some(id => !BabyRecordSchema.shape.id.safeParse(id).success)) throw new Error('Invalid retired baby identities.');
  return [...new Set(value)] as string[];
}
export function parsePage(value: unknown, householdId: string, cursor: string): Page {
  const page = value as Page;
  if (!page || !Array.isArray(page.changes) || page.changes.length > 500 || typeof page.has_more !== 'boolean'
    || typeof page.next_cursor !== 'string' || !cursorPattern.test(page.next_cursor) || BigInt(page.next_cursor) < BigInt(cursor)) throw new Error('Invalid delta page.');
  let prior = BigInt(cursor);
  const changes = page.changes.map(row => {
    if (!row || row.household_id !== householdId || !['baby', 'event'].includes(row.entity_type)
      || typeof row.seq !== 'string' || !cursorPattern.test(row.seq) || BigInt(row.seq) <= prior) throw new Error('Invalid delta order/scope.');
    prior = BigInt(row.seq);
    const record = recordFromWire(row.entity_type, row.record, householdId);
    if (record.id !== row.entity_id) throw new Error('Delta identity mismatch.');
    return { ...row, record };
  });
  if (prior !== BigInt(page.next_cursor) || (page.has_more && changes.length === 0)) throw new Error('Invalid delta cursor.');
  return { ...page, changes, retired_baby_ids: parseRetiredBabies(page.retired_baby_ids) };
}
